import { useCallback, useEffect, useRef } from 'react';
import { DrawableAudioPlayer } from 'utils/audio/types';
import { secToMillis } from 'utils/time';
import { useTracking } from './context';
import { MediaEventTracker } from './types';

interface UsePlayerMediaTrackingConfig {
  player: DrawableAudioPlayer | undefined;
  unitId: string | undefined;
}

/**
 * subscribes to player events to fire corresponding media events.  while the media
 * event functions exposed via context can be called manually, they are generally tied
 * to HTMLMediaElement events which are exposed to the app via the Player object. this
 * hook provides an easy way to subscribe to the Player's events and fire off
 * media events.
 */
export default function usePlayerMediaTracking({
  player,
  unitId,
}: UsePlayerMediaTrackingConfig) {
  const {
    trackMediaBufferCompleted,
    trackMediaBufferStarted,
    trackMediaCompleted,
    trackMediaPaused,
    trackMediaPlaybackHeartbeat,
    trackMediaPlaybackStarted,
  } = useTracking();

  const bufferingRef = useRef(false);

  const callTracker = useCallback(
    (tracker: MediaEventTracker) => {
      if (player && unitId) {
        tracker(unitId, secToMillis(player.currentTime()));
      }
    },
    [player, unitId],
  );

  useEffect(() => {
    if (player) {
      const handlePlaying = () => callTracker(trackMediaPlaybackStarted);
      const handlePaused = () => callTracker(trackMediaPaused);
      const handleTimeupdate = () => {
        if (!player.paused) {
          callTracker(trackMediaPlaybackHeartbeat);
        }
      };
      const handleEnded = () => callTracker(trackMediaCompleted);
      const handleWaiting = () => {
        bufferingRef.current = true;
        callTracker(trackMediaBufferStarted);
      };
      const handleCanPlay = () => {
        if (bufferingRef.current) {
          bufferingRef.current = false;
          callTracker(trackMediaBufferCompleted);
        }
      };

      player.on('playing', handlePlaying);
      player.on('pause', handlePaused);
      player.on('timeupdate', handleTimeupdate);
      player.on('ended', handleEnded);
      player.on('waiting', handleWaiting);
      player.on('canplay', handleCanPlay);

      return () => {
        player.off('playing', handlePlaying);
        player.off('pause', handlePaused);
        player.off('timeupdate', handleTimeupdate);
        player.off('ended', handleEnded);
        player.off('waiting', handleWaiting);
        player.off('canplay', handleCanPlay);
      };
    }
  }, [
    callTracker,
    player,
    trackMediaBufferCompleted,
    trackMediaBufferStarted,
    trackMediaCompleted,
    trackMediaPaused,
    trackMediaPlaybackHeartbeat,
    trackMediaPlaybackStarted,
  ]);
}
